import { useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';
import { getVisibleReadingLocator } from '../utils/readingProgressUtils';

const THROTTLE_MS = 2000;
const DETECT_DELAY_MS = 300;
const IDLE_TIMEOUT_MS = 500;

interface UseReadingProgressFromPageParams {
  currentPageRef: React.RefObject<HTMLDivElement | null>;
  sceneId: string | undefined;
  updateReadingProgress: (params: {
    sceneId: string;
    body: { episode_id: string; paragraph_index: number; char_offset: number };
  }) => void;
  currentIndex: number;
}

export const useReadingProgressFromPage = ({
  currentPageRef,
  sceneId,
  updateReadingProgress,
  currentIndex
}: UseReadingProgressFromPageParams): void => {
  const lastSentKeyRef = useRef<string>('');
  const pendingLocatorRef = useRef<ReturnType<typeof getVisibleReadingLocator>>(null);

  const sendReadingProgress = useMemo(
    () =>
      throttle(
        () => {
          const loc = pendingLocatorRef.current;
          if (!loc || !sceneId) return;
          updateReadingProgress({
            sceneId,
            body: {
              episode_id: loc.episodeId,
              paragraph_index: loc.paragraphIndex,
              char_offset: loc.charOffset
            }
          });
        },
        THROTTLE_MS,
        { leading: true, trailing: true }
      ),
    [sceneId, updateReadingProgress]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-detect when page changes; currentPageRef is stable
  useEffect(() => {
    let idleId: number | undefined;

    const detectAndSend = () => {
      const container = currentPageRef.current;
      if (!container) return;

      const runDetection = () => {
        const loc = getVisibleReadingLocator(container);
        if (loc) {
          const key = `${loc.episodeId}-${loc.paragraphIndex}-${loc.charOffset}`;
          if (lastSentKeyRef.current !== key) {
            lastSentKeyRef.current = key;
            pendingLocatorRef.current = loc;
            sendReadingProgress();
          }
        }
      };

      if (typeof requestIdleCallback !== 'undefined') {
        idleId = requestIdleCallback(runDetection, { timeout: IDLE_TIMEOUT_MS });
      } else {
        runDetection();
      }
    };

    const timer = setTimeout(detectAndSend, DETECT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      if (idleId !== undefined && typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(idleId);
      }
      sendReadingProgress.cancel();
    };
  }, [currentIndex, sendReadingProgress]);
};
